import { useEffect, useState } from "react";
import { AcquisitionSource } from "../features/auth/types/acquisitionSource";
import { captureInvitationCodeFromUrl } from "../utility/invitationCode";

const ACQUISITION_KEY = "acquisition_source";
const ACQUISITION_EXPIRY_KEY = "acquisition_source_expiry";
const EXPIRY_DAYS = 30; // Data expires after 30 days

/**
 * Hook to track and persist user acquisition source from UTM parameters
 * Captures UTM parameters on first visit and stores them in localStorage
 * Data persists across sessions for 30 days
 */
export const useAcquisitionTracking = () => {
  const [acquisitionSource, setAcquisitionSource] = useState<AcquisitionSource | null>(null);

  useEffect(() => {
    const captureAcquisitionData = () => {
      // Capture invitation code from URL if present
      const urlParams = new URLSearchParams(window.location.search);
      captureInvitationCodeFromUrl(urlParams);
      
      // Check if we already have valid stored data
      const storedData = localStorage.getItem(ACQUISITION_KEY);
      const expiryDate = localStorage.getItem(ACQUISITION_EXPIRY_KEY);
      
      if (storedData && expiryDate) {
        const expiry = new Date(expiryDate);
        if (expiry > new Date()) {
          // Valid stored data exists
          setAcquisitionSource(JSON.parse(storedData));
          return;
        } else {
          // Data expired, clear it
          localStorage.removeItem(ACQUISITION_KEY);
          localStorage.removeItem(ACQUISITION_EXPIRY_KEY);
        }
      }

      // Capture new acquisition data from URL
      
      const source = urlParams.get("utm_source") || undefined;
      const medium = urlParams.get("utm_medium") || undefined;
      const campaign = urlParams.get("utm_campaign") || undefined;
      const content = urlParams.get("utm_content") || undefined;
      const term = urlParams.get("utm_term") || undefined;
      const fbclid = urlParams.get("fbclid") || undefined;
      const gclid = urlParams.get("gclid") || undefined;
      
      // Get referrer (only if it's from a different domain)
      let referrer: string | undefined = undefined;
      if (document.referrer) {
        try {
          const referrerUrl = new URL(document.referrer);
          const currentUrl = new URL(window.location.href);
          if (referrerUrl.hostname !== currentUrl.hostname) {
            referrer = document.referrer;
          }
        } catch (e) {
          // Invalid URL, ignore
        }
      }

      // Only store if we have at least one piece of data
      if (source || medium || campaign || fbclid || gclid || referrer) {
        const data: AcquisitionSource = {
          source,
          medium,
          campaign,
          content,
          term,
          landingPage: window.location.pathname,
          referrer,
          fbclid,
          gclid,
        };

        // Store in localStorage with expiry
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + EXPIRY_DAYS);
        
        localStorage.setItem(ACQUISITION_KEY, JSON.stringify(data));
        localStorage.setItem(ACQUISITION_EXPIRY_KEY, expiryDate.toISOString());
        
        setAcquisitionSource(data);
      } else if (storedData) {
        // No new data, use stored data if available
        setAcquisitionSource(JSON.parse(storedData));
      }
    };

    captureAcquisitionData();
  }, []);

  /**
   * Get the current acquisition source data
   */
  const getAcquisitionSource = (): AcquisitionSource | null => {
    const storedData = localStorage.getItem(ACQUISITION_KEY);
    const expiryDate = localStorage.getItem(ACQUISITION_EXPIRY_KEY);
    
    if (storedData && expiryDate) {
      const expiry = new Date(expiryDate);
      if (expiry > new Date()) {
        return JSON.parse(storedData);
      }
    }
    return null;
  };

  /**
   * Clear the stored acquisition data
   */
  const clearAcquisitionSource = () => {
    localStorage.removeItem(ACQUISITION_KEY);
    localStorage.removeItem(ACQUISITION_EXPIRY_KEY);
    setAcquisitionSource(null);
  };

  return {
    acquisitionSource,
    getAcquisitionSource,
    clearAcquisitionSource,
  };
};
